import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrganization } from '../../context/organization';
import { trackNavClick, trackNavExternalClick, trackLogout } from '../../analytics';
import './index.css';

const SIDEBAR_WIDTH_REM = 12; // keep CSS in sync

const Navbar: React.FC = () => {
  const { logout, user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { organization, loading } = useOrganization();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const isCorporateUser = organization?.me.is_corporate;
  const hasSdkAccess = organization?.has_sdk_license;
  const adminRoutes = ['/satellite-image-tasks', '/admin/datapoint-pricing'];
  const reportsRoutes = ['/reports/population', '/reports/road-traffic'];
  const isJoinRoute = /^\/join\/.+/.test(location.pathname);
  const isAdminRouteSelected = adminRoutes.includes(location.pathname);
  const isReportsRouteSelected = reportsRoutes.includes(location.pathname);

  const isSelected = useCallback(
    (path: string) => (location.pathname === path ? 'is-active' : ''),
    [location.pathname]
  );

  // Escape & outside clicks
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
        setIsSidebarOpen(false);
        setAccordionOpen(false);
        setReportsOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // Add body padding only when sidebar/topbar are present
  useEffect(() => {
    const hasShell = isAuthenticated && !!organization;
    if (hasShell) {
      document.body.classList.add('has-sidebar');
      document.documentElement.style.setProperty('--sidebar-w', `${SIDEBAR_WIDTH_REM}rem`);
    } else {
      document.body.classList.remove('has-sidebar');
      document.documentElement.style.removeProperty('--sidebar-w');
    }
    return () => {
      document.body.classList.remove('has-sidebar');
      document.documentElement.style.removeProperty('--sidebar-w');
    };
  }, [isAuthenticated, organization]);

  const handleNavigation = (path: string, label: string, navLocation = 'sidebar') => {
    trackNavClick({ destination_path: path, nav_label: label, nav_location: navLocation });
    navigate(path);
    setAccordionOpen(false);
    setReportsOpen(false);
    setIsSidebarOpen(false);
  };

  const toggleSidebar = () => setIsSidebarOpen((s) => !s);

  const toggleAccordion = () => {
    setAccordionOpen((s) => !s);
  };
  const toggleReports = () => {
    setReportsOpen((s) => !s);
  };

  return (
    <>
      {/* Top Nav */}
      <nav className="fixed top-0 z-50 w-full bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-slate-100">
        <div className="w-full px-3 sm:px-4">
          <div className="flex h-14 items-center justify-between">
            {/* Left: burger + brand */}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleSidebar}
                type="button"
                className="inline-flex items-center justify-center rounded-xl p-2 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:hidden"
                aria-label="Open sidebar"
                aria-expanded={isSidebarOpen}
              >
                <svg className="h-6 w-6" aria-hidden="true" viewBox="0 0 20 20" fill="currentColor">
                  <path clipRule="evenodd" fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z" />
                </svg>
              </button>

              <button
                onClick={() => organization && handleNavigation('/', 'dashboard', 'top_nav')}
                className="group flex items-center"
                aria-label="Go to dashboard"
              >
                <span className="text-xl font-extrabold tracking-tight bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-500 bg-clip-text text-transparent">
                  CivAPI
                </span>
                <span className="ml-2 rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 hidden sm:inline-block group-hover:translate-y-px transition">
                  Dashboard
                </span>
              </button>
            </div>

            {/* Right: Org chip + avatar */}
            {isAuthenticated && (
              <div className="relative flex items-center gap-3">
                <div className="hidden sm:flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-500"></span>
                  <span className="text-sm font-semibold text-slate-800 truncate max-w-[200px]">
                    {organization?.name || ''}
                  </span>
                </div>

                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen((p) => !p)}
                    className="flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-slate-200 bg-white hover:ring-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-haspopup="menu"
                    aria-expanded={isDropdownOpen}
                  >
                    {loading && !isJoinRoute ? (
                      <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200" />
                    ) : (
                      <img
                        className="h-8 w-8 rounded-full object-cover"
                        src={organization?.me?.profile_pic_url || user?.picture}
                        alt="user avatar"
                        referrerPolicy="no-referrer"
                      />
                    )}
                  </button>

                  {isDropdownOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-3 w-56 origin-top-right rounded-2xl border border-slate-100 bg-white p-2 shadow-xl ring-1 ring-black/5"
                    >
                      <div className="px-3 py-2">
                        <p className="text-xs text-slate-500">Signed in as</p>
                        <p className="truncate text-sm font-medium text-slate-800">
                          {organization?.me?.email || user?.email}
                        </p>
                      </div>
                      <div className="my-1 h-px bg-slate-100" />
                      <button
                        onClick={() => {
                          trackLogout({ logout_location: 'navbar' });
                          logout({ logoutParams: { returnTo: window.location.origin } });
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 focus:bg-slate-50"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 13v-2H7V8l-5 4 5 4v-3z" />
                          <path d="M20 3h-8a2 2 0 0 0-2 2v3h2V5h8v14h-8v-3h-2v3a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z" />
                        </svg>
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Sidebar + overlay */}
      {isAuthenticated && organization && (
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 transition-opacity sm:hidden ${isSidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden="true"
          />
          <aside
            aria-label="Sidebar"
            className={`fixed top-0 left-0 h-screen w-72 pt-16 sm:pt-20 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-r border-slate-100 shadow-lg sm:shadow-none transition-transform duration-300 will-change-transform ${
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'
            }`}
          >
            <div className="flex h-full flex-col justify-between overflow-y-auto px-3 pb-6">
              <ul className="space-y-1.5 font-medium">
                <li>
                  <button onClick={() => handleNavigation('/', 'api_keys')} className={`nav-item ${isSelected('/')}`}>
                    <span className="nav-accent" />
                    <span className="nav-icon">
                      {isSelected('/') ? (
                        <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      )}
                    </span>
                    <span className="nav-label">API Keys</span>
                  </button>
                </li>

                <li>
                  <button onClick={() => handleNavigation('/credits', 'credits')} className={`sub-item ${isSelected('/credits')}`}>
                    <svg className={`h-5 w-5 ${isSelected('/credits') ? 'text-blue-600' : 'text-slate-600'}`} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22,9V19a1,1,0,0,1-1,1H7a1,1,0,0,1-1-1V18H19.5a.5.5,0,0,0,.5-.5V8h1A1,1,0,0,1,22,9ZM2,15V5A1,1,0,0,1,3,4H17a1,1,0,0,1,1,1V15a1,1,0,0,1-1,1H3A1,1,0,0,1,2,15Z" />
                    </svg>
                    <span className="sub-label">Credits</span>
                  </button>
                </li>

                <li>
                  <button onClick={() => handleNavigation('/query', 'query')} className={`nav-item ${isSelected('/query')}`}>
                    <span className="nav-accent" />
                    <span className="nav-icon">
                      <svg className={`h-5 w-5 ${isSelected('/query') ? 'text-blue-600' : 'text-slate-600'}`} viewBox="0 0 22 21" fill="currentColor">
                        <path d="M16.975 11H10V4.025a1 1 0 0 0-1.066-.998 8.5 8.5 0 1 0 9.039 9.039.999.999 0 0 0-1-1.066h.002Z" />
                        <path d="M12.5 0c-.157 0-.311.01-.565.027A1 1 0 0 0 11 1.02V10h8.975a1 1 0 0 0 1-.935c.013-.188.028-.374.028-.565A8.51 8.51 0 0 0 12.5 0Z" />
                      </svg>
                    </span>
                    <span className="nav-label">Query</span>
                  </button>
                </li>

                {/* Reports */}
                <li>
                  <button
                    onClick={toggleReports}
                    className={`nav-item ${isReportsRouteSelected ? 'is-active' : ''}`}
                    aria-expanded={reportsOpen}
                    aria-controls="reports-accordion"
                  >
                    <span className="nav-accent" />
                    <span className="nav-icon">
                      {/* Better "reports" icon: Bar Chart with spaces between bars */}
                      <svg
                        className={`h-5 w-5 ${isReportsRouteSelected ? 'text-blue-600' : 'text-slate-600'}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3"  y="12" width="3" height="8"  rx="1" fill="currentColor" />
                        <rect x="9"  y="8"  width="3" height="12" rx="1" fill="currentColor" />
                        <rect x="15" y="4"  width="3" height="16" rx="1" fill="currentColor" />
                        {/* spaces between bars by reducing width and increasing x spacing */}
                      </svg>
                    </span>
                    <span className="nav-label">Reports</span>
                    <svg
                      className={`ml-auto h-4 w-4 transition-transform ${reportsOpen || isReportsRouteSelected ? 'rotate-180' : ''}`}
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  <div
                    id="reports-accordion"
                    className={`overflow-hidden transition-[max-height,opacity] duration-300 ${reportsOpen || isReportsRouteSelected ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <ul className="mt-1 space-y-1 pl-3">
                      <li>
                        <button
                          onClick={() => handleNavigation('/reports/population', 'reports_population')}
                          className={`sub-item ${isSelected('/reports/population')}`}
                        >
                          <span className="nav-dot" />
                          <svg
                            className={`h-4 w-4 ${isSelected('/reports/population') ? 'text-blue-600' : 'text-slate-600'}`}
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M4 4h16v2H4zm0 4h16v2H4zm0 4h10v2H4zm0 4h8v2H4z" />
                          </svg>
                          <span className="sub-label">Population</span>
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => handleNavigation('/reports/road-traffic', 'reports_road_traffic')}
                          className={`sub-item ${isSelected('/reports/road-traffic')}`}
                        >
                          <span className="nav-dot" />
                          <svg
                            className={`h-4 w-4 ${isSelected('/reports/road-traffic') ? 'text-blue-600' : 'text-slate-600'}`}
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M5 16l2-8h10l2 8h-2l-1-4H8l-1 4H5zm3 4a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm8 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
                          </svg>
                          <span className="sub-label">Road Traffic</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                </li>

                {/* Satellite Image Tasks */}
                <li>
                  <button
                    onClick={() => handleNavigation('/satellite/tasks', 'satellite_tasks')}
                    className={`nav-item ${isSelected('/satellite/tasks')}`}
                  >
                    <span className="nav-accent" />
                    <span className="nav-icon">
                      {isSelected('/satellite/tasks') ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-22" viewBox="0 0 512 512" xmlSpace="preserve" width="22" height="22">
                          <circle fill="#80D6FB" cx="256" cy="256" r="233" />
                          <path
                            fill="#46BEE8"
                            d="M429.948 82.052C383.485 35.588 321.709 10 256 10S128.515 35.588 82.052 82.052C35.588 128.515 10 190.291 10 256s25.588 127.485 72.052 173.948C128.515 476.412 190.291 502 256 502s127.485-25.588 173.948-72.052C476.411 383.485 502 321.709 502 256s-25.589-127.485-72.052-173.948zM36.395 269h81.18c.802 27.949 4.184 55.051 9.936 80.37H56.827C45.226 324.72 38.069 297.586 36.395 269zM269 136.63V37.52c23.808 5.569 46.295 26.344 64.382 59.893 6.47 12.002 12.11 25.156 16.871 39.217H269zm88.796 26c6.143 25.037 9.764 52.191 10.617 80.37H269v-80.37h88.796zM243 37.521v99.11h-81.253c4.761-14.061 10.401-27.215 16.871-39.217C196.705 63.865 219.192 43.09 243 37.521zm0 125.109V243h-99.413c.853-28.179 4.474-55.333 10.617-80.37H243zM117.574 243h-81.18c1.674-28.586 8.831-55.72 20.433-80.37h70.683c-5.751 25.319-9.134 52.421-9.936 80.37zm26.013 26H243v80.37h-88.796c-6.144-25.037-9.765-52.191-10.617-80.37zM243 375.37v99.11c-23.808-5.57-46.295-26.344-64.382-59.893-6.47-12.002-12.11-25.156-16.871-39.217H243zm26 99.109v-99.11h81.253c-4.761 14.061-10.401 27.215-16.871 39.217-18.087 33.549-40.574 54.324-64.382 59.893zm0-125.109V269h99.413c-.853 28.179-4.474 55.333-10.617 80.37H269zM394.426 269h81.18c-1.674 28.586-8.831 55.72-20.433 80.37H384.49c5.752-25.319 9.134-52.421 9.936-80.37zm0-26c-.802-27.949-4.184-55.051-9.936-80.37h70.683c11.602 24.649 18.758 51.783 20.433 80.37h-81.18zm46.288-106.37h-63.159c-5.744-18.608-12.871-35.943-21.288-51.555-7.309-13.557-15.365-25.406-24.021-35.444 44.836 16.618 82.823 47.451 108.468 86.999zM179.753 49.631c-8.656 10.038-16.712 21.887-24.021 35.444-8.417 15.613-15.544 32.947-21.288 51.555H71.286c25.645-39.548 63.632-70.381 108.467-86.999zM71.286 375.37h63.159c5.744 18.608 12.871 35.943 21.288 51.555 7.309 13.557 15.365 25.406 24.021 35.444-44.836-16.618-82.823-47.451-108.468-86.999zm260.961 86.999c8.656-10.038 16.712-21.887 24.021-35.444 8.417-15.613 15.544-32.947 21.288-51.555h63.159c-25.646 39.548-63.633 70.381-108.468 86.999z"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-22"
                          viewBox="0 0 120 120"
                          xmlSpace="preserve"
                          width="22"
                          height="22"
                        >
                          <switch>
                            <g>
                              <path d="M60 120C26.9 120 0 93.1 0 60S26.9 0 60 0s60 26.9 60 60-26.9 60-60 60zM60 5C29.7 5 5 29.7 5 60s24.7 55 55 55 55-24.7 55-55S90.3 5 60 5z" />
                              <path d="M60 120c-19.3 0-34.4-26.4-34.4-60S40.7 0 60 0s34.4 26.4 34.4 60-15.1 60-34.4 60zM60 5C43.8 5 30.5 29.7 30.5 60s13.2 55 29.5 55 29.5-24.7 29.5-55S76.2 5 60 5z" />
                              <path d="M12.2 25.6h95.6v5H12.2zM12.2 89.5h95.6v5H12.2zM2.5 57.5h115v5H2.5z" />
                              <path d="M57.5 2.5h5v115h-5z" />
                            </g>
                          </switch>
                        </svg>
                      )}
                    </span>
                    <span className="nav-label">Satellite Image Tasks</span>
                  </button>
                </li>

                <li>
                  <button onClick={() => handleNavigation('/members', 'members')} className={`nav-item ${isSelected('/members')}`}>
                    <span className="nav-accent" />
                    <span className="nav-icon">
                      <svg className={`h-5 w-5 ${isSelected('/members') ? 'text-blue-600' : 'text-slate-600'}`} viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                    </span>
                    <span className="nav-label">Members</span>
                  </button>
                </li>

                <li>
                  <button onClick={() => handleNavigation('/acled-license', 'acled_license')} className={`nav-item ${isSelected('/acled-license')}`}>
                    <span className="nav-accent" />
                    <span className="nav-icon">
                      <svg
                        className={`h-5 w-5 ${isSelected('/acled-license') ? 'text-blue-600' : 'text-slate-600'}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
                        <line x1="8" y1="6" x2="16" y2="6"/>
                        <line x1="8" y1="10" x2="16" y2="10"/>
                        <line x1="8" y1="14" x2="14" y2="14"/>
                      </svg>
                    </span>
                    <span className="nav-label">ACLED License</span>
                  </button>
                </li>

                {/* Admin dropdown */}
                {isCorporateUser && (
                  <li>
                    <button
                      onClick={() => setAccordionOpen((s) => !s)}
                      className={`nav-item ${accordionOpen || isAdminRouteSelected ? 'is-active' : ''}`}
                      aria-expanded={accordionOpen}
                      aria-controls="admin-accordion"
                    >
                      <span className="nav-accent" />
                      <span className="nav-icon">
                        <svg
                          className={`h-5 w-5 ${accordionOpen ? 'text-blue-600' : 'text-slate-600'}`}
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M12 2a5 5 0 015 5v1h1a3 3 0 013 3v7a3 3 0 01-3 3H6a3 3 0 01-3-3v-7a3 3 0 013-3h1V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v1h6V7a3 3 0 00-3-3z" />
                        </svg>
                      </span>
                      <span className="nav-label">Admin</span>
                      <svg
                        className={`ml-auto h-4 w-4 transition-transform ${accordionOpen ? 'rotate-180' : ''}`}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    <div
                      id="admin-accordion"
                      className={`overflow-hidden transition-[max-height,opacity] duration-300 ${accordionOpen || isAdminRouteSelected ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <ul className="mt-1 space-y-1 pl-3">
                        <li>
                          <button
                            onClick={() => handleNavigation('/satellite-image-tasks', 'satellite_tasks_admin')}
                            className={`sub-item ${isSelected('/satellite-image-tasks')}`}
                          >
                            <span className="nav-dot" />
                            <svg
                              className={`h-4 w-4 ${isSelected('/satellite-image-tasks') ? 'text-blue-600' : 'text-slate-600'}`}
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm1 15h-2v-2h2Zm0-4h-2V7h2Z" />
                            </svg>
                            <span className="sub-label">Satellite: All Users Tasks</span>
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => handleNavigation('/admin/datapoint-pricing', 'datapoint_pricing')}
                            className={`sub-item ${isSelected('/admin/datapoint-pricing')}`}
                          >
                            <span className="nav-dot" />
                            <svg
                              className={`h-4 w-4 ${isSelected('/admin/datapoint-pricing') ? 'text-blue-600' : 'text-slate-600'}`}
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M4 4h16v2H4zm2 5h12v2H6zm-2 5h16v2H4zm2 5h12v2H6z" />
                            </svg>
                            <span className="sub-label">Datapoint Pricing</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </li>
                )}

                {hasSdkAccess && (
                  <>
                    <li className="pt-2">
                      <div className="px-4 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">SDK</div>
                    </li>
                    <li>
                      <button onClick={() => handleNavigation('/sdk/configuration', 'sdk_configuration')} className={`nav-item ${isSelected('/sdk/configuration')}`}>
                        <span className="nav-accent" />
                        <span className="nav-icon">
                          <svg className={`h-5 w-5 ${isSelected('/sdk/configuration') ? 'text-blue-600' : 'text-slate-600'}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M5 3h14a2 2 0 0 1 2 2v5H3V5a2 2 0 0 1 2-2zm-2 9h18v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" opacity=".2"/>
                            <path d="M19 2H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V5a3 3 0 0 0-3-3zm1 17a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-4h16zm0-6H4V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1z"/>
                            <path d="M7 15h4v2H7zM13 15h4v2h-4z" />
                          </svg>
                        </span>
                        <span className="nav-label">SDK Configuration</span>
                      </button>
                    </li>
                    <li>
                      <button onClick={() => handleNavigation('/sdk/contexts', 'sdk_contexts')} className={`nav-item ${isSelected('/sdk/contexts')}`}>
                        <span className="nav-accent" />
                        <span className="nav-icon">
                          <svg className={`h-5 w-5 ${isSelected('/sdk/contexts') ? 'text-blue-600' : 'text-slate-600'}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4 4h16v4H4z" opacity=".2"/>
                            <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1zm-1 16H5V9h14zm0-12H5V5h14z"/>
                            <path d="M7 11h6v2H7zm0 4h4v2H7zm6 0h4v2h-4z" />
                          </svg>
                        </span>
                        <span className="nav-label">SDK Contexts</span>
                      </button>
                    </li>
                  </>
                )}

                <li className="pt-2">
                  <div className="px-4 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Resources</div>
                </li>
                <li>
                  <a
                    href="https://docs.civapi.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackNavExternalClick({ external_url: 'https://docs.civapi.com', nav_label: 'api_docs' })}
                    className="nav-item external"
                  >
                    <span className="nav-accent" />
                    <span className="nav-icon">
                      <svg className="h-5 w-5 text-slate-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M4 2h14a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm0 2v16h14V4H4zm2 2h10v2H6V6zm0 4h10v2H6v-2zm0 4h10v2H6v-2z" />
                      </svg>
                    </span>
                    <span className="nav-label">API Docs</span>
                    <svg className="ml-auto h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="15 3 21 3 21 9" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="10" y1="14" x2="21" y2="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>

                <li>
                  <a
                    href="https://static.civapi.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackNavExternalClick({ external_url: 'https://static.civapi.com/privacy-policy', nav_label: 'privacy_policy' })
                    }
                    className="nav-item external"
                  >
                    <span className="nav-accent" />
                    <span className="nav-icon">
                      <svg className="h-5 w-5 text-slate-600" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C12 2 4 4 4 8.5C4 17 12 22 12 22C12 22 20 17 20 8.5C20 4 12 2 12 2Z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round" />
                        <path d="M12 11.5V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="9" r="1" fill="currentColor" />
                      </svg>
                    </span>
                    <span className="nav-label">Privacy Policy</span>
                    <svg className="ml-auto h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="15 3 21 3 21 9" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="10" y1="14" x2="21" y2="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>

                <li>
                  <a
                    href="https://static.civapi.com/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackNavExternalClick({ external_url: 'https://static.civapi.com/terms-of-use', nav_label: 'terms_of_use' })
                    }
                    className="nav-item external"
                  >
                    <span className="nav-accent" />
                    <span className="nav-icon">
                      <svg className="h-5 w-5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="3" fill="#fff" stroke="#4B5563"/>
                        <path d="M8 7h8M8 11h8M8 15h4" stroke="#4B5563"/>
                        <circle cx="7" cy="7" r="1" fill="#4B5563"/>
                        <circle cx="7" cy="11" r="1" fill="#4B5563"/>
                        <circle cx="7" cy="15" r="1" fill="#4B5563"/>
                      </svg>
                    </span>
                    <span className="nav-label">Terms of Use</span>
                    <svg className="ml-auto h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="15 3 21 3 21 9" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="10" y1="14" x2="21" y2="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>

              <div className="mt-6 hidden sm:flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-600">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm1 15h-2v-2h2Zm0-4h-2V7h2Z"/></svg>
                Press <kbd className="rounded border bg-white px-1">Esc</kbd> to close panels
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export default Navbar;
